import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";

const OfferClicksAndViews = () => {
  const { offerViewsAndClicks } = useSelector(
    (state) => state?.merchantReducer
  );

  const [state, setState] = useState({
    series: [
      { name: "Clicks", data: [] },
      { name: "Views", data: [] },
      { name: "Redeemed", data: [] },
    ],
    xaxisCategories: [],
  });

  const [maxDataValue, setMaxDataValue] = useState(10);
  const [step, setStep] = useState(2);
  const [chartOptions, setChartOptions] = useState({
    colors: ["#3C50E0", "#80CAEE", "#49EA80"],
    chart: {
      type: "bar",
      height: 335,
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    plotOptions: {
      bar: {
        columnWidth: "35%",
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: [],
      labels: {
        rotate: 0,
        trim: true,
        hideOverlappingLabels: false,
        style: { fontSize: "12px", whiteSpace: "nowrap" },
      },
      title: {
        text: "Offers",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
      position: "bottom",
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const clicks = series[0]?.[dataPointIndex] || 0;
        const views = series[1]?.[dataPointIndex] || 0;
        const redeemed = series[2]?.[dataPointIndex] || 0;
        const title = w.globals.labels[dataPointIndex] || "Unknown Offer";
        
        const ctr = views ? Math.min(100, (clicks / views) * 100) : (clicks > 0 ? 100 : 0);
        const rr = clicks ? Math.min(100, (redeemed / clicks) * 100) : (redeemed > 0 ? 100 : 0);
        
        return `
          <div class="p-3 font-poppins" style="background: rgba(255, 255, 255, 0.98); border: 1px solid #e2e8f0; border-radius: 6px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); color: #1c2434; font-size: 12px; line-height: 1.5;">
            <div style="font-weight: 600; font-size: 13px; margin-bottom: 8px; color: #1C2434;">${title}</div>
            <div style="display: flex; flex-direction: column; gap: 4px;">
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: #64748B;">Views:</span>
                <span style="font-weight: 500;">${views}</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: #64748B;">Clicks:</span>
                <span style="font-weight: 500;">${clicks}</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px; border-bottom: 1px solid #E2E8F0; padding-bottom: 4px; margin-bottom: 4px;">
                <span style="color: #64748B;">Redeemed:</span>
                <span style="font-weight: 500;">${redeemed}</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: #64748B; font-weight: 600;">CTR:</span>
                <span style="font-weight: 700; color: #3C50E0;">${ctr.toFixed(1)}%</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: #64748B; font-weight: 600;">Redemption Rate:</span>
                <span style="font-weight: 700; color: #10B981;">${rr.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        `;
      }
    },
    yaxis: {
      min: 0,
      max: 10,
      tickAmount: 5,
      title: {
        text: "Views, Clicks & Redeemed",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
      labels: {
        formatter: (value) => value,
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      fontSize: "14px",
    },
    fill: { opacity: 1 },
  });

  useEffect(() => {
    if (offerViewsAndClicks?.data?.length > 0) {
      const clicksData = offerViewsAndClicks.data.map((offer) => offer.clicks || 0);
      const viewsData = offerViewsAndClicks.data.map((offer) => offer.views || 0);
      const redemptionData = offerViewsAndClicks.data.map((offer) => offer?.redemptions || offer?.redemptionCount || 0);
      const titles = offerViewsAndClicks.data.map((offer) => offer.title || "Unknown Offer");

      const maxClicks = Math.max(...clicksData);
      const maxViews = Math.max(...viewsData);
      const maxRedemptions = Math.max(...redemptionData);
      const maxData = Math.max(maxClicks, maxViews, maxRedemptions);

      const newMax = Math.max(maxData, 5);
      const newStep = Math.ceil(newMax / 5);

      setMaxDataValue(newMax);
      setStep(newStep);

      setState({
        series: [
          { name: "Clicks", data: clicksData },
          { name: "Views", data: viewsData },
          { name: "Redeemed", data: redemptionData },
        ],
        xaxisCategories: titles,
      });
      const offerCount = offerViewsAndClicks.data.length;

      let columnWidth;
      if (offerCount <= 3) {
        columnWidth = "35%";
      } else if (offerCount <= 7) {
        columnWidth = "55%";
      } else {
        columnWidth = "65%";
      }

      setChartOptions((prev) => ({
        ...prev,
        plotOptions: {
          bar: {
            columnWidth: columnWidth,
            distributed: false,
          },
        },
        xaxis: { ...prev.xaxis, categories: titles },
        yaxis: {
          ...prev.yaxis,
          max: Math.ceil(newMax / newStep) * newStep,
          tickAmount: Math.ceil(newMax / newStep),
        },
      }));
    }
  }, [offerViewsAndClicks]);

  return (
    <div className="rounded-sm border border-stroke bg-white px-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 summary offersChart">
      <div className="flex flex-col sm:flex-col xl:flex-row justify-between w-full mt-4 mb-4">
        <span className="text-[20px] font-poppins font-[600] text-[black] whitespace-nowrap">
          Offer Views - Clicks - Redeemed
        </span>
      </div>
      {state.series[0]?.data.length > 0 || state.series[1]?.data?.length > 0 || state.series[2]?.data?.length > 0 ? (
        <div className="overflow-x-auto">
          <div id="chartTwo" className="min-w-[10rem]">
            <ReactApexChart
              options={chartOptions}
              series={state.series}
              type="bar"
              height={350}
            />
          </div>
        </div>
      ) : (
        <div className="w-full h-[200px] flex items-center justify-center">
          <div id="chartTwo" className="min-w-[700px]">
            <div className="text-center text-gray-500 text-lg font-medium py-10">
              No Clicks, Views, or Redeemed Found
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferClicksAndViews;
